import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

const EASE = [0.22, 1, 0.36, 1];
const container = { hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } } };
const item = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } } };

/**
 * VerticalHero — shared hero for every /for-* spoke page.
 *
 * Renders:
 *   - Persistent "Back to homepage" breadcrumb (top-left, small)
 *   - Eyebrow (vertical name, e.g. "For Restaurants")
 *   - H1
 *   - Tagline
 *   - Primary + secondary CTA
 */
export default function VerticalHero({
  eyebrow,
  h1,
  tagline,
  primaryCta,
  secondaryCta,
  testId = 'vertical-hero',
}) {
  return (
    <section
      className="relative w-full overflow-hidden px-4 sm:px-6 pt-10 pb-14 md:pt-14 md:pb-20 bg-card border-b border-border"
      data-testid={testId}
    >
      <div aria-hidden className="absolute inset-0 pointer-events-none hc-northlines opacity-40" />

      <div className="relative z-10 max-w-4xl mx-auto">
        <Link
          to="/"
          data-testid="vertical-hero-back-home"
          className="inline-flex items-center gap-1.5 text-xs md:text-sm font-semibold text-muted-foreground hover:text-primary transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
          Back to homepage
        </Link>

        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="mt-8 text-left"
        >
          <motion.div variants={item} className="flex items-center gap-3 mb-5">
            <span aria-hidden className="h-4 w-1 bg-primary" />
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-primary">
              {eyebrow}
            </p>
          </motion.div>

          <motion.h1
            variants={item}
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black tracking-tighter text-foreground leading-[1.02]"
          >
            {h1}
          </motion.h1>

          <motion.p
            variants={item}
            className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl"
          >
            {tagline}
          </motion.p>

          {(primaryCta || secondaryCta) && (
            <motion.div variants={item} className="mt-8 flex flex-wrap items-center gap-3">
              {primaryCta && (
                <a
                  href={primaryCta.href}
                  data-testid={primaryCta.testId || 'vertical-hero-primary-cta'}
                  className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-primary-hover transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
                >
                  {primaryCta.label}
                </a>
              )}
              {secondaryCta && (
                <a
                  href={secondaryCta.href}
                  data-testid={secondaryCta.testId || 'vertical-hero-secondary-cta'}
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-border bg-background text-foreground font-semibold hover:border-primary hover:text-primary transition-colors"
                >
                  {secondaryCta.label}
                </a>
              )}
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
